import { useCallback, useEffect, useState } from "react";
import { decodeAccessToken, hasRecentMfaSession } from "./mfaSessionJwt";

export type MfaSessionStatus = {
  /** null while the first check is in flight */
  enrolled: boolean | null;
  /** null while checking; false when enrolled but JWT is not recent aal2 */
  sessionReady: boolean | null;
  /** enrolled and recent MFA session — safe to unlock write UI */
  ready: boolean;
  refresh: () => Promise<void>;
};

/**
 * Tracks TOTP enrollment and whether the current JWT has a recent MFA session.
 * Use `ready` to unlock forms that must not be editable before step-up.
 *
 * Standard UX: see docs/account-sensitive-settings.md
 * ("Session MFA UX standard") — never let users enter protected data before MFA.
 */
export function useMfaSessionStatus(): MfaSessionStatus {
  const [enrolled, setEnrolled] = useState<boolean | null>(null);
  const [sessionReady, setSessionReady] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    const adapter = window.sessionAdapter;
    if (!adapter) {
      setEnrolled(false);
      setSessionReady(false);
      return;
    }
    try {
      const result = await adapter.hasTotpFactor();
      const isEnrolled = result.error ? false : Boolean(result.data?.enrolled);
      setEnrolled(isEnrolled);
      if (!isEnrolled) {
        setSessionReady(false);
        return;
      }
      const session = await adapter.getSession();
      const jwt = decodeAccessToken(session?.access_token);
      setSessionReady(hasRecentMfaSession(jwt));
    } catch {
      setEnrolled(false);
      setSessionReady(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    enrolled,
    sessionReady,
    ready: enrolled === true && sessionReady === true,
    refresh,
  };
}

/** @deprecated Prefer {@link useMfaSessionStatus} */
export function useMfaEnrollmentStatus() {
  const { enrolled, refresh } = useMfaSessionStatus();
  return { enrolled, refresh };
}
